import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import GlassCard from '../../components/ui/GlassCard';
import NeonButton from '../../components/ui/NeonButton';
import PageTransition from '../../components/animations/PageTransition';
import { coursesAPI, learningAPI, analyticsAPI } from '../../services/api';
import { getCourseThumbnail } from '../../utils/courseMedia';

const StudentDashboard = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [courses, setCourses] = useState([]);
    const [enrollments, setEnrollments] = useState([]);
    const [profile, setProfile] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fallbackProfile = {
            xp: parseInt(localStorage.getItem('totalXP')) || 120,
            level: 1,
            streak: 3,
            zen_coins: parseInt(localStorage.getItem('totalZenCoins')) || 50,
            words_learned: 24,
            study_time_minutes: 45
        };

        const fetchData = async () => {
            try {
                // Fetch all data in parallel with resilient fallbacks
                const [coursesRes, enrollmentsRes, summaryRes, leaderboardRes] = await Promise.all([
                    coursesAPI.getAll().catch(() => ({ data: [] })),
                    learningAPI.getEnrollments().catch(() => ({ data: { enrollments: [] } })),
                    analyticsAPI.getDashboardSummary().catch(() => ({ data: { stats: fallbackProfile } })),
                    learningAPI.getLeaderboard(10).catch(() => ({ data: { leaderboard: [] } }))
                ]);
                
                setCourses(coursesRes.data || []);
                setEnrollments(enrollmentsRes.data?.enrollments || enrollmentsRes.data || []);
                
                if (summaryRes.data && summaryRes.data.stats) {
                    setProfile(summaryRes.data.stats);
                } else {
                    setProfile(fallbackProfile);
                }
                
                const leaderboardData = leaderboardRes.data?.leaderboard || leaderboardRes.data || [];
                setLeaderboard(Array.isArray(leaderboardData) ? leaderboardData : []);
            } catch (err) {
                console.error("Failed to fetch dashboard data, using local profile:", err);
                setProfile(fallbackProfile);
            }
        };

        const userData = JSON.parse(localStorage.getItem('user') || 'null');
        setUser(userData || { name: 'Student', role: 'student' });
        fetchData();
    }, [refreshTrigger]);

    const activeProfile = profile || {
        xp: parseInt(localStorage.getItem('totalXP')) || 120,
        level: 1,
        streak: 3,
        zen_coins: parseInt(localStorage.getItem('totalZenCoins')) || 50
    };

    // Get active course from enrollments or fallback to first course
    const activeCourse = enrollments.length > 0 
        ? courses.find(c => c.id === enrollments[0].course_id) 
        : courses[0];
    
    // Calculate progress to next level
    // Calculate progress to next level (500 XP per level)
    const currentXP = activeProfile.xp || 0;
    const currentLevel = activeProfile.level || 1;
    const nextLevelXP = currentLevel * 500;
    const prevLevelXP = (currentLevel - 1) * 500;
    const levelProgressXP = currentXP - prevLevelXP;
    const levelTotalXPNeeded = 500;
    const levelProgress = Math.min(100, Math.max(0, (levelProgressXP / levelTotalXPNeeded) * 100)).toFixed(0);
    
    const activeCourseThumbnail = getCourseThumbnail(activeCourse);
    
    // Get real progress for active course if available
    const activeEnrollment = enrollments.find(e => e.course_id === activeCourse?.id);
    const courseProgress = activeEnrollment?.progress || 0; 
    const lastLessonTitle = activeEnrollment ? `Resuming ${activeCourse?.title || 'Lessons'}` : "Start your learning journey today!";

    return (
        <PageTransition>
            <div style={{ background: 'var(--bg-dark)', minHeight: '100vh', paddingBottom: '60px' }}>
                {/* The MainLayout usually handles Navbar, but if we need a custom dashboard header we can add it here. 
                For now, we rely on MainLayout wrapping this component in App.js */}

                <div className="container" style={{ paddingTop: '0px' }}>

                    {/* WELCOME SECTION */}
                    <header style={{ marginBottom: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap' }}>
                        <div>
                            <h1 className="text-gradient" style={{ fontSize: '2.5rem', marginBottom: '10px' }}>
                                Welcome back, {user.name?.split(' ')[0]}!
                            </h1>
                            <p style={{ color: '#94a3b8', fontSize: '1.1rem' }}>
                                You're on a <strong>{activeProfile.streak || 0}-day streak</strong>. Keep it up! 🔥
                            </p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '5px' }}>Next Goal</div>
                            <div style={{ fontSize: '1.2rem', color: 'white', fontWeight: 'bold' }}>Reach Level {(activeProfile.level || 1) + 1}</div>
                            <div style={{ width: '150px', height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px', marginTop: '5px' }}>
                                <div style={{ width: `${levelProgress}%`, height: '100%', background: 'var(--primary)', borderRadius: '3px' }}></div>
                            </div>
                        </div>
                    </header>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 2.2fr) minmax(0, 1fr)', gap: '30px' }}>

                        {/* LEFT COLUMN: LEARNING & COURSES */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>

                            {/* HERO: CONTINUE LEARNING */}
                            <GlassCard
                                hoverEffect={true}
                                style={{
                                    background: 'linear-gradient(135deg, rgba(39, 176, 91, 0.1) 0%, rgba(39, 176, 91, 0.05) 100%)',
                                    border: '1px solid rgba(39, 176, 91, 0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    flexWrap: 'wrap'
                                }}
                                onClick={() => activeCourse && navigate(`/courses/${activeCourse.id}`)}
                            >
                                <div style={{
                                    width: '120px',
                                    height: '120px',
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                                    flexShrink: 0
                                }}>
                                    {activeCourseThumbnail && (
                                        <img
                                            src={activeCourseThumbnail}
                                            alt="Course"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    )}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '5px' }}>
                                        RESUME LEARNING
                                    </div>
                                    <h2 style={{ color: 'white', margin: '0 0 10px', fontSize: '1.8rem' }}>
                                        {activeCourse?.title || "Loading..."}
                                    </h2>
                                    <p style={{ color: '#cbd5e1', marginBottom: '20px' }}>
                                        {activeEnrollment ? `Last Lesson: ${lastLessonTitle}` : "Start your learning journey today!"}
                                    </p>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                        <div style={{ flex: 1, background: 'rgba(255,255,255,0.1)', height: '8px', borderRadius: '4px' }}>
                                            <div style={{ width: `${courseProgress}%`, background: '#4ade80', height: '100%', borderRadius: '4px', boxShadow: '0 0 10px rgba(74, 222, 128, 0.5)' }}></div>
                                        </div>
                                        <span style={{ color: 'white', fontWeight: 'bold' }}>{courseProgress}%</span>
                                    </div>
                                </div>
                                <div style={{ alignSelf: 'center' }}>
                                    <NeonButton onClick={(e) => { e.stopPropagation(); navigate(`/courses/${activeCourse?.id}`); }}>
                                        ▶ Continue
                                    </NeonButton>
                                </div>
                            </GlassCard>

                            {/* RECOMMENDED COURSES GRID */}
                            <div>
                                <h3 style={{ color: 'white', marginBottom: '20px' }}>Recommended For You</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '20px' }}>
                                    {courses.slice(1, 4).map((course, i) => (
                                        <GlassCard key={i} hoverEffect={true} style={{ padding: '20px' }} onClick={() => navigate(`/courses/${course.id}`)}>
                                            <div style={{ height: '140px', borderRadius: '12px', background: '#1e293b', marginBottom: '15px', overflow: 'hidden' }}>
                                                {getCourseThumbnail(course) && (
                                                    <img src={getCourseThumbnail(course)} alt={course.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                )}
                                            </div>
                                            <h4 style={{ color: 'white', marginBottom: '5px' }}>{course.title}</h4>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: '0.85rem' }}>
                                                <span>{course.level || 'Intermediate'}</span>
                                                <span>⭐ {course.rating || '4.8'}</span>
                                            </div>
                                        </GlassCard>
                                    ))}
                                </div>
                            </div>

                        </div>

                        {/* RIGHT COLUMN: STATS & SOCIAL */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>

                            {/* DAILY GOAL */}
                            <GlassCard style={{ background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                    <h3 style={{ color: 'white', margin: 0 }}>Daily Goal</h3>
                                    <div style={{ background: 'rgba(245, 158, 11, 0.2)', padding: '4px 10px', borderRadius: '12px', color: '#fbbf24', fontSize: '0.8rem', fontWeight: 'bold' }}>
                                        +50 XP
                                    </div>
                                </div>
                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#cbd5e1', fontSize: '0.9rem', marginBottom: '5px' }}>
                                        <span>{levelProgressXP} / {levelTotalXPNeeded} XP to Next Level</span>
                                        <span>{levelProgress}%</span>
                                    </div>
                                    <div style={{ background: 'rgba(255,255,255,0.1)', height: '10px', borderRadius: '5px' }}>
                                        <div style={{ width: `${levelProgress}%`, background: '#fbbf24', height: '100%', borderRadius: '5px', transition: 'width 0.8s ease-out' }}></div>
                                    </div>
                                </div>
                                <NeonButton style={{ width: '100%', background: '#d97706' }} onClick={async () => {
                                    try {
                                        await learningAPI.completeActivity('quick_practice', 100);
                                        setRefreshTrigger(prev => prev + 1);
                                        window.dispatchEvent(new Event('zencoins-updated'));
                                    } catch (e) {
                                        navigate('/practice');
                                    }
                                }}>
                                    Complete Practice (Get XP!)
                                </NeonButton>
                            </GlassCard>

                            {/* LEADERBOARD SNIPPET */}
                            <GlassCard>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                    <h3 style={{ color: 'white', margin: 0 }}>Leaderboard</h3>
                                    <span style={{ color: 'var(--primary)', cursor: 'pointer', fontSize: '0.9rem' }} onClick={() => navigate('/rewards')}>View All</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    {Array.isArray(leaderboard) && leaderboard.slice(0, 3).map((u, i) => {
                                        const myId = user.user_id || user.id;
                                        const thisId = u.user_id || u.id;
                                        const isCurrentUser = myId && thisId && myId === thisId;
                                        
                                        return (
                                            <div key={i} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '10px',
                                                background: isCurrentUser ? 'rgba(39, 176, 91, 0.1)' : 'transparent',
                                                borderRadius: '12px',
                                                border: isCurrentUser ? '1px solid var(--primary-glow)' : 'none'
                                            }}>
                                                <div style={{ color: u.rank === 1 ? '#fbbf24' : '#94a3b8', fontWeight: 'bold', width: '30px' }}>#{u.rank}</div>
                                                <div style={{ flex: 1, color: 'white', fontWeight: isCurrentUser ? 'bold' : 'normal' }}>
                                                    {isCurrentUser ? 'You' : (u.name || u.email?.split('@')[0])}
                                                </div>
                                                <div style={{ color: 'var(--primary)', fontWeight: 'bold' }}>{u.zen_coins}</div>
                                            </div>
                                        );
                                    })}
                                    {(!Array.isArray(leaderboard) || leaderboard.length === 0) && (
                                        <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
                                            Complete lessons to join the leaderboard!
                                        </div>
                                    )}
                                </div>
                            </GlassCard>

                        </div>
                    </div>
                </div>
            </div>

        </PageTransition >
    );
};

export default StudentDashboard;
